import {BaseTileProvider} from './base-tile-provider';
import {MapType} from '../../enums/map-type';
import {MapProvider} from '../../enums/map-provider.enum';
import {SettingsService} from '../../services/settings.service';
import {ThemeId} from '../../../common/enums/theme-id';
import {environment} from '../../../../environments/environment';

/**
 * HERE, through its Raster Tile API v3 (maps.hereapi.com) with the API key in
 * the environment files (hereApiKey). The legacy Map Tile API with an
 * app_id/app_code pair, which the app used before, answers 410 Gone.
 *
 * The key travels in every tile URL -- the browser asks HERE for each tile --
 * so it is limited to the app's sites in the HERE platform rather than kept
 * secret. Without a key HERE is not offered at all (map-source-catalog.ts).
 */
export class HereMapsTileProvider extends BaseTileProvider {

  // Styles and sizes as GET /v3/info lists them; 512 px tiles cover the same
  // ground as 256 px ones, at twice the resolution.
  private static URL_HERE = 'https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/{f}?style={s}&size={sz}&apiKey={k}';

  static isConfigured(): boolean {
    return !!environment.hereApiKey;
  }

  constructor(private settings: SettingsService) {
    super();
  }

  getMapProvider(): string {
    return MapProvider.HERE_MAPS;
  }

  getMaxZoom(): number {
    // HERE's highest level; BaseTileProvider stretches its tiles beyond it.
    return 20;
  }

  getUrlTemplate(mapType?: MapType): string {
    const mt = mapType || SettingsService.settings.mapType;
    // The street and terrain maps follow the app's theme, as Google's do.
    const time = this.settings.local.theme === ThemeId.LIGHT ? 'day' : 'night';
    let style = `explore.${time}`;
    let format = 'png8';
    switch (mt) {
      case MapType.SATELLITE:
        style = 'satellite.day';
        format = 'jpeg';
        break;
      case MapType.HYBRID:
        // Imagery with HERE's roads and labels drawn on it.
        style = 'explore.satellite.day';
        format = 'jpeg';
        break;
      case MapType.TERRAIN:
        style = `topo.${time}`;
        break;
    }
    return HereMapsTileProvider.URL_HERE
      .replace('{f}', format)
      .replace('{s}', style)
      .replace('{sz}', this.settings.local.showHDMaps ? '512' : '256')
      .replace('{k}', encodeURIComponent(environment.hereApiKey || ''));
  }

  getUrl(x, y, zoom, mapType?: MapType): string {
    return this.getUrlTemplate(mapType)
      .replace('{z}', zoom)
      .replace('{x}', x)
      .replace('{y}', y);
  }
}
